import { useState } from "react";
import { Icon } from "@iconify/react";
import { SlideOver } from "@/shared/components/dialog";
import { Switch } from "@/shared/components/switch";
import { Button } from "@/shared/components/button";
import { cn } from "@/shared/lib/utils";
import { requestMoreCategoriesApi } from "@/portals/caregiver/api/caregiverApi";
import { toastError, toastSuccess } from "@/shared/lib/toast";
import { CATEGORY_ICONS } from "@/features/patient/family/components/InviteCaregiverModal";
import type { CaregiverCategoryValue } from "@/shared/types/family";
import type { NotSharedCategory } from "@/shared/types/caregiverPortal";

interface RequestMoreAccessModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  patientName: string;
  /** Only categories the caregiver doesn't already have — never the ones already shared. */
  notSharedCategories: NotSharedCategory[];
  onRequested: () => void;
}

export function RequestMoreAccessModal({
  open,
  onOpenChange,
  patientId,
  patientName,
  notSharedCategories,
  onRequested,
}: RequestMoreAccessModalProps) {
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedCategories = notSharedCategories.filter((c) => selected[c.value]).map((c) => c.value);
  const canSubmit = selectedCategories.length > 0 && !isSubmitting;
  const allSelected = notSharedCategories.length > 0 && notSharedCategories.every((c) => selected[c.value]);
  const toggleSelectAll = () => {
    setSelected(allSelected ? {} : Object.fromEntries(notSharedCategories.map((c) => [c.value, true])));
  };

  const close = () => {
    onOpenChange(false);
    setSelected({});
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    const { error } = await requestMoreCategoriesApi(patientId, selectedCategories as CaregiverCategoryValue[]);
    setIsSubmitting(false);

    if (error) {
      toastError(error);
      return;
    }

    onRequested();
    toastSuccess(`Request sent to ${patientName}`);
    close();
  };

  return (
    <SlideOver open={open} onOpenChange={close} width={480}>
      <div className="p-7 pb-28">
        <div className="mb-1 flex items-center gap-1.5 pr-10">
          <span className="h-1.5 w-1.5 rounded-full bg-[#E4002B]" />
          <span className="text-[11px] font-bold uppercase tracking-widest text-[#E4002B]">Additional Access</span>
        </div>
        <h2 className="text-xl font-extrabold text-[#0A2947]">Request More Access</h2>
        <p className="mt-1 text-sm text-[#6B7280]">
          Only categories {patientName} hasn't already shared with you show up here. {patientName} will approve or decline each request.
        </p>

        <div className="my-5 h-px bg-[#E5E7EB]" />

        {notSharedCategories.length === 0 ? (
          <p className="rounded-2xl border border-[#E5E7EB] bg-[#F9FAFB] p-4 text-center text-sm text-[#9CA3AF]">
            {patientName} has already shared everything they can.
          </p>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-[#E5E7EB]">
            <div className="flex items-center justify-between gap-3 border-b border-[#E5E7EB] px-4 py-3">
              <p className="text-[11px] font-bold uppercase tracking-wide text-[#9CA3AF]">Categories</p>
              <button
                type="button"
                onClick={toggleSelectAll}
                className="shrink-0 text-xs font-bold text-[#E4002B] hover:underline"
              >
                {allSelected ? "Deselect All" : "Select All"}
              </button>
            </div>
            <div className="divide-y divide-[#E5E7EB]">
              {notSharedCategories.map((category) => {
                const enabled = Boolean(selected[category.value]);
                return (
                  <div
                    key={category.value}
                    className={cn(
                      "flex items-center justify-between gap-3 border-l-4 px-4 py-3",
                      enabled ? "border-[#F36F8F] bg-[#FFF0F3]" : "border-transparent",
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-lg", enabled ? "bg-[#F36F8F]" : "bg-[#F3F4F6]")}>
                        <Icon icon={CATEGORY_ICONS[category.value]} className={cn("h-4 w-4", enabled ? "text-white" : "text-[#6B7280]")} />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#0A2947]">{category.label}</p>
                        <p className="text-xs text-[#9CA3AF]">{category.description}</p>
                      </div>
                    </div>
                    <Switch
                      checked={enabled}
                      onCheckedChange={() => setSelected((current) => ({ ...current, [category.value]: !current[category.value] }))}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-5 flex items-start gap-3 rounded-2xl border border-[#F36F8F]/40 bg-[#FFF7F8] p-4">
          <Icon icon="lucide:info" className="mt-0.5 h-4 w-4 shrink-0 text-[#E4002B]" />
          <p className="text-xs leading-relaxed text-[#6B7280]">
            Nothing is shared automatically. {patientName} sees exactly which categories you're asking for and can approve or decline each one.
          </p>
        </div>
      </div>

      <div className="sticky bottom-0 flex justify-between gap-3 border-t border-[#E5E7EB] bg-white px-7 py-4">
        <Button variant="link" onClick={close} disabled={isSubmitting} className="!h-auto !p-0 !text-sm !font-semibold !text-[#6B7280] hover:!text-[#0A2947]">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          loading={isSubmitting}
          className="!h-auto !rounded-lg !border-transparent !bg-[#E4002B]/50 !px-5 !py-2.5 !text-sm !font-semibold !text-white hover:!border-transparent hover:!bg-[#E4002B]/60 hover:!text-white"
        >
          Send Request
        </Button>
      </div>
    </SlideOver>
  );
}
